import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Modal,
  SpaceBetween,
  Spinner,
  Table,
  TableProps,
  Textarea,
  TextFilter,
} from "@amzn/awsui-components-react/polaris";

import { RootState } from "../../app/rootReducer";
import { trackButtonClick } from "../../telemetry/telemetry";
import {
  BulkPassExceptionRow,
  buildBulkPassExceptionRows,
  closeModal,
  fetchFailed,
  fetchSucceeded,
  MAX_BULK_PASS_EXCEPTION_ASINS,
  submitBulkPassExceptions,
} from "./submitPassExceptionsConnector";
import { fetchAllPagesForCurrentQuery } from "./fetchAllPagesForCurrentQuery";

const REASON_MAX_LENGTH = 128;

const COLUMN_DEFINITIONS: TableProps.ColumnDefinition<BulkPassExceptionRow>[] =
  [
    {
      id: "asin",
      header: "ASIN",
      cell: (row) => row.asin,
      sortingField: "asin",
      width: 130,
    },
    {
      id: "programName",
      header: "Program",
      cell: (row) => row.programName || "-",
      sortingField: "programName",
      width: 140,
    },
    {
      id: "marketplace",
      header: "MP",
      cell: (row) => row.marketplace,
      sortingField: "marketplace",
      width: 80,
    },
    {
      id: "service",
      header: "Service",
      cell: (row) => row.service,
      sortingField: "service",
      width: 100,
    },
    {
      id: "step",
      header: "Validation Step",
      cell: (row) => row.step,
      sortingField: "step",
      width: 220,
    },
    {
      id: "reason",
      header: "Reason",
      cell: (row) => row.reason || "-",
    },
  ];

function matchesFilter(row: BulkPassExceptionRow, needle: string): boolean {
  if (!needle) {
    return true;
  }
  const n = needle.toLowerCase();
  return (
    row.asin.toLowerCase().includes(n) ||
    row.step.toLowerCase().includes(n) ||
    row.reason.toLowerCase().includes(n) ||
    row.programName.toLowerCase().includes(n) ||
    row.marketplace.toLowerCase().includes(n) ||
    row.service.toLowerCase().includes(n)
  );
}

export const SubmitPassExceptionsModal: React.FC = () => {
  const dispatch = useDispatch();
  const {
    isOpen,
    mode,
    workingAsins,
    isFetching,
    fetchError,
    fetchTruncated,
    isSubmitting,
    submissionCompleted,
    submissionSucceeded,
  } = useSelector((state: RootState) => state.submitPassExceptions);
  const queryString = useSelector(
    (state: RootState) => state.generateQuery.queryString
  );
  const editableAttributes = useSelector(
    (state: RootState) => state.porAsinList.editableAttributes
  );

  const [selectedRows, setSelectedRows] = useState<BulkPassExceptionRow[]>([]);
  const [reason, setReason] = useState<string>("");
  const [filterText, setFilterText] = useState<string>("");
  const abortRef = useRef<AbortController | null>(null);

  const userIsAuthorized = useMemo(
    () =>
      editableAttributes.some(
        (attr) =>
          attr.attributeName === "create_pass_exception" && attr.authorized
      ),
    [editableAttributes]
  );

  useEffect(() => {
    if (!isOpen) {
      setSelectedRows([]);
      setReason("");
      setFilterText("");
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (!isFetching) {
      return;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    let cancelled = false;
    fetchAllPagesForCurrentQuery({
      queryString: queryString ?? "",
      signal: controller.signal,
      maxAsins: MAX_BULK_PASS_EXCEPTION_ASINS,
    })
      .then(({ rows, truncated }) => {
        if (cancelled) {
          return;
        }
        dispatch(fetchSucceeded({ asins: rows, truncated }));
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        const message =
          err instanceof Error && err.name === "AbortError"
            ? "Fetch cancelled or timed out"
            : "Failed to load matching ASINs";
        dispatch(fetchFailed({ message }));
      });
    return () => {
      cancelled = true;
      controller.abort();
      abortRef.current = null;
    };
  }, [isOpen, isFetching, queryString, dispatch]);

  const allRows = useMemo(
    () => buildBulkPassExceptionRows(workingAsins),
    [workingAsins]
  );
  const visibleRows = useMemo(
    () => allRows.filter((row) => matchesFilter(row, filterText.trim())),
    [allRows, filterText]
  );

  const reasonTrimmed = reason.trim();
  const reasonTooLong = reason.length > REASON_MAX_LENGTH;
  const reasonEmpty = reasonTrimmed.length === 0;
  const submitDisabledReason: string | undefined = !userIsAuthorized
    ? "You do not have permission to create pass exceptions."
    : isFetching
    ? "Loading ASINs data"
    : selectedRows.length === 0
    ? "Select at least one attribute to submit."
    : reasonEmpty
    ? "Enter a pass exception reason."
    : reasonTooLong
    ? `Reason exceeds ${REASON_MAX_LENGTH} character limit.`
    : undefined;
  const submitDisabled = submitDisabledReason !== undefined || isSubmitting;

  const modeLabel =
    mode === "one_time" ? "One-Time Pass Exceptions" : "Pass Exceptions";
  const title = `Submit ${modeLabel} (${allRows.length} attribute${
    allRows.length === 1 ? "" : "s"
  })`;

  const handleClose = () => {
    dispatch(closeModal());
  };

  const handleSubmit = () => {
    trackButtonClick("passException", "SubmitBulk", {
      selectedCount: selectedRows.length,
      totalCount: allRows.length,
      mode,
    });
    dispatch(submitBulkPassExceptions(selectedRows, reasonTrimmed, mode));
  };

  // Only auto-close on partial or full success. A total failure keeps
  // the modal open so the user can retry without re-selecting; the
  // flashbar carries the error detail.
  useEffect(() => {
    if (submissionCompleted && !isSubmitting && submissionSucceeded) {
      dispatch(closeModal());
    }
  }, [submissionCompleted, isSubmitting, submissionSucceeded, dispatch]);

  return (
    <Modal
      data-testid="submit-pass-exceptions-modal"
      visible={isOpen}
      onDismiss={handleClose}
      size="max"
      closeAriaLabel="Close bulk pass exceptions modal"
      header={title}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="link"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={submitDisabled}
              disabledReason={submitDisabledReason}
              loading={isSubmitting}
              onClick={handleSubmit}
              data-testid="submit-pass-exceptions-modal-submit"
            >
              Submit {modeLabel} ({selectedRows.length})
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="s" direction="vertical">
        {!userIsAuthorized && (
          <Alert type="warning" statusIconAriaLabel="Warning">
            You do not have permissions to create pass exceptions. Please open a
            ticket to the LEAP team to request access.
          </Alert>
        )}
        {fetchError && (
          <Alert type="error" statusIconAriaLabel="Error">
            {fetchError}
          </Alert>
        )}
        {fetchTruncated && !isFetching && (
          <Alert type="warning" statusIconAriaLabel="Warning">
            Only the first{" "}
            {MAX_BULK_PASS_EXCEPTION_ASINS.toLocaleString("en-US")} ASINs were
            loaded. Narrow the filter to include the remaining ASINs in a
            submission.
          </Alert>
        )}
        {isFetching ? (
          <Box textAlign="center" padding="l">
            <SpaceBetween size="s" direction="vertical" alignItems="center">
              <Spinner size="large" />
              <span>Loading ASINs data</span>
            </SpaceBetween>
          </Box>
        ) : (
          <>
            <TextFilter
              filteringText={filterText}
              filteringPlaceholder="Filter by ASIN, step, or reason..."
              filteringAriaLabel="Filter attributes"
              onChange={({ detail }) => setFilterText(detail.filteringText)}
              countText={`${visibleRows.length} match${
                visibleRows.length === 1 ? "" : "es"
              }`}
            />
            <Table
              data-testid="submit-pass-exceptions-modal-table"
              variant="embedded"
              selectionType="multi"
              trackBy="key"
              items={visibleRows}
              selectedItems={selectedRows}
              onSelectionChange={({ detail }) =>
                setSelectedRows(detail.selectedItems)
              }
              columnDefinitions={COLUMN_DEFINITIONS}
              stickyHeader={true}
              resizableColumns={true}
              ariaLabels={{
                selectionGroupLabel: "Attribute selection",
                allItemsSelectionLabel: ({ selectedItems }) =>
                  `${selectedItems.length} attributes selected`,
                itemSelectionLabel: (_data, row) =>
                  `Select ${row.asin} ${row.service} ${row.step}`,
              }}
              empty={
                <Box textAlign="center" padding="m" color="text-body-secondary">
                  {allRows.length === 0
                    ? "No failed validation attributes in the current scope."
                    : "No attributes match the current filter."}
                </Box>
              }
            />
            <Box>
              <Box variant="awsui-key-label">
                Pass Exception Reason (required, applies to all selected)
              </Box>
              <Textarea
                data-testid="submit-pass-exceptions-modal-reason"
                value={reason}
                onChange={({ detail }) => setReason(detail.value)}
                placeholder="Enter a reason for the pass exception"
                rows={3}
                invalid={reason.length > 0 && (reasonEmpty || reasonTooLong)}
              />
              {reason.length > REASON_MAX_LENGTH && (
                <Box color="text-status-error" fontSize="body-s">
                  Reason exceeds {REASON_MAX_LENGTH} character limit.
                </Box>
              )}
            </Box>
          </>
        )}
      </SpaceBetween>
    </Modal>
  );
};

export default SubmitPassExceptionsModal;
